import React from 'react';
import { Link } from 'react-router-dom';
import './ApproachPage.css';

const terms = [
  {
    mech: '▸ Compensation',
    title: <>Tied to your performance.</>,
    body: 'Success fees against agreed performance, or profit sharing over an agreed period. Some clients prefer a monthly retainer with a reduced performance fee. If the approach does not deliver measurable results, we do not earn fees for them.',
  },
  {
    mech: '▸ What counts',
    title: <>Bottom-line improvement.</>,
    body: 'Success means net profit, cash flow, return on investment. The kind of result you can point to on a statement. Workshop attendance does not qualify. Neither does a transformation deck.',
  },
];

const prerequisites = [
  {
    title: 'Leadership is bought in.',
    body: 'The conviction has to live with the people who own the outcome. A project manager cannot substitute for that.',
  },
  {
    title: 'The entity operates as an independent system.',
    body: 'It manages its own cash flow, makes its own decisions, sets its own strategy, owns its structure. We work with parent companies, subsidiaries, and the occasional self-sustaining division that meets these.',
  },
  {
    title: 'Leadership can protect 4-hour strategy sessions.',
    body: 'Without the executive team being interrupted. The quiet prerequisite most firms skip past. If you cannot carve out uninterrupted strategic time, that is the first constraint worth naming.',
  },
];

const processSteps = [
  { numeral: 'I',   title: 'Immerse',            body: 'Live the business with your leaders. Your goals, your numbers, your lived reality.', artifact: 'Shared reality' },
  { numeral: 'II',  title: 'Locate & design',    body: 'Find what is holding the business back. Design the fix with your team.',              artifact: 'Named constraint' },
  { numeral: 'III', title: 'Implement together', body: 'Hands-on alongside your team. We train by doing the work together.',                  artifact: 'Working fix' },
  { numeral: 'IV',  title: 'Verify',             body: 'Measure against real performance. No results, no fees.',                              artifact: 'Proof in numbers' },
  { numeral: 'V',   title: 'Step away',          body: 'Exit when your team can find the next constraint without us.',                        artifact: 'Self-sustaining team' },
];

const editorial = [
  {
    image: '/img/approachPage/btpwf.webp',
    num: 'i · Continuity',
    title: <>Building the ship while sailing.</>,
    paragraphs: [
      'Your business continues operating throughout. We work within your existing rhythms. Scheduled sessions, site reviews, decisions that respect your operational priorities. Any significant change needs your call on timing.',
      'Knowledge transfers through hands-on collaboration. Your people learn by doing. That is the only way the improvement outlasts us.',
    ],
  },
  {
    image: '/img/approachPage/partnership.webp',
    reverse: true,
    num: 'ii · Commercial',
    title: <>Compensation tied to your numbers.</>,
    paragraphs: [
      'We succeed when you succeed. Our compensation comes from sharing in the improvements we help create. Success fees are tied to performance or to profit sharing over an agreed period. Some clients prefer a monthly retainer with a reduced performance fee.',
      'Success means bottom-line improvement. Net profit, cash flow, ROI. The commercial arrangement is shaped around each client’s situation.',
    ],
  },
  {
    image: '/img/approachPage/system.webp',
    num: 'iii · Systems',
    title: <>Systems and teams. One fabric.</>,
    paragraphs: [
      'Every business runs through interconnected systems, driven by teams. When the systems work together and the teams are aligned to the outcome, operations flow. When they don’t, everything feels like crisis management.',
      'We help you see those connections and locate the specific constraint. We work with the team structures and motivations you already have. The strong parts of your culture become the fix. Patterns that last are the ones your people built with us, not the ones we imposed on them.',
    ],
  },
  {
    image: '/img/approachPage/success.webp',
    reverse: true,
    num: 'iv · Outcome',
    title: <>What success looks like.</>,
    paragraphs: [
      'Operations feel quieter. Less firefighting, fewer late-night calls. Teams work toward shared outcomes instead of departmental wins. Problems get solved faster and stay solved.',
      'Financial performance improves in ways you can point to on a statement. Your team identifies the next constraint and addresses it without us. Where a specific challenge needs expertise beyond operational improvement, our partner network covers the specialisation.',
    ],
  },
];

const fitYes = [
  'Medium-sized, with leadership that owns the outcome.',
  'Able to protect 4-hour strategy sessions when they are needed.',
  'Operating as an independent system with its own cash flow.',
];
const fitNo = [
  'Quick fixes. Symptomatic work that leaves the system as it is.',
  'Prescriptive solutions handed down without your team building them.',
  'Crisis interventions that need to resolve this quarter.',
];

const ApproachPage = () => {
  return (
    <div className="approach-page">
      {/* ===== HERO ===== */}
      <section className="ip-hero">
        <div className="ip-hero__motif" aria-hidden="true"></div>
        <div className="frame">
          <div className="ip-hero__inner">
            <div className="eyebrow-rule">I · Approach</div>
            <div className="ip-hero__wordmark">RussellStone Lighthouse</div>
            <h1 className="ip-hero__title">How we work, and what we charge for.</h1>
            <p className="ip-hero__subcap">An engagement model built around outcomes.</p>
            <p className="ip-hero__lede">
              When a business is stuck, the cause is rarely effort. It is the system shaping the effort. We find the one constraint limiting everything else, fix it alongside your team, and leave when your people can find the next one themselves.
            </p>
            <div className="ip-hero__cta">
              <Link to="/contact"    className="btn btn--primary">Start a conversation <span className="arrow"></span></Link>
              <Link to="/principles" className="btn btn--ghost">Read the principles</Link>
            </div>
          </div>
        </div>
        <div className="ip-hero__horizon" aria-hidden="true"></div>
      </section>

      {/* ===== TERMS GRID ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">II</div>
              <div className="sidehead__cat">The terms</div>
            </aside>
            <div>
              <h2 className="sidehead__title">We succeed when <em>you</em> do.</h2>
              <p className="sidehead__lede">
                Our compensation comes from sharing in the improvements we help create. Two parts to the arrangement: how we are paid, and what counts as a result. Each engagement is shaped around the client’s situation.
              </p>

              <div className="terms-grid terms-grid--two">
                {terms.map((t) => (
                  <article key={t.mech} className="terms-grid__cell">
                    <div className="terms-grid__mech">{t.mech}</div>
                    <h3 className="terms-grid__title">{t.title}</h3>
                    <p className="terms-grid__body">{t.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PREREQUISITES ===== */}
      <section className="section band--prussian stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">III</div>
              <div className="sidehead__cat">Fit · prerequisites</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Three prerequisites for good work.</h2>
              <p className="sidehead__lede">
                Three preconditions sit underneath every engagement. We check for them in the first conversation. If they are not yet in place, the first work is to make them so.
              </p>

              <div className="prereq">
                {prerequisites.map((p, i) => (
                  <div key={p.title} className="prereq__row">
                    <div className="prereq__num">{String(i + 1).padStart(2, '0')}</div>
                    <div>
                      <h4 className="prereq__title">{p.title}</h4>
                      <p className="prereq__body">{p.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FIVE MOVEMENTS ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead approach-process__head">
            <aside className="sidehead__label">
              <div className="sidehead__roman">IV</div>
              <div className="sidehead__cat">The engagement</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Five stages. In order.</h2>
              <p className="sidehead__lede">
                Each movement protects the next from the most common failure mode of the one before it. Your business continues operating throughout.
              </p>
            </div>
          </div>

          <div className="process--sectional">
            {processSteps.map((s) => (
              <div key={s.numeral} className="step">
                <div className="step__pip">{s.numeral}</div>
                <h4 className="step__title">{s.title}</h4>
                <p className="step__body">{s.body}</p>
                <span className="step__artifact">{s.artifact}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== EDITORIAL ===== */}
      <section className="section band--prussian stitch--top">
        <div className="frame">
          <div className="sidehead approach-editorial__head">
            <aside className="sidehead__label">
              <div className="sidehead__roman">V</div>
              <div className="sidehead__cat">In practice</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Inside an engagement.</h2>
              <p className="sidehead__lede">
                Four passages from inside an engagement. Each is a small claim about how the partnership behaves when it is working.
              </p>
            </div>
          </div>

          {editorial.map((e) => (
            <article key={e.num} className={`editorial${e.reverse ? ' editorial--reverse' : ''}`}>
              <div className="editorial__media" aria-hidden="true">
                <img src={e.image} alt="" className="editorial__img" />
              </div>
              <div>
                <div className="editorial__num">{e.num}</div>
                <h3 className="editorial__title">{e.title}</h3>
                <div className="editorial__body">
                  {e.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ===== FIT ===== */}
      <section className="section band--paper stitch--top">
        <div className="column">
          <div className="eyebrow-rule">VI · Fit</div>
          <h2 className="sidehead__title approach-fit__title">
            Is this <em>right</em> for your business?
          </h2>
          <p className="sidehead__lede approach-fit__lede">
            We work best with medium-sized businesses whose leadership is committed to the work and can protect the time it takes. We take on a limited number of clients depending on intensity. If we are not the right fit, we would rather know early. Often we know someone who is.
          </p>

          <div className="fit-strip">
            <div className="fit-strip__col">
              <div className="fit-strip__head">— Right fit</div>
              <ul className="fit-strip__list">
                {fitYes.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="fit-strip__col fit-strip__col--no">
              <div className="fit-strip__head">— Not the work for us</div>
              <ul className="fit-strip__list">
                {fitNo.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="final-cta">
        <div className="final-cta__lines"></div>
        <div className="stage">
          <div className="eyebrow-rule">VII · Start</div>
          <h2 className="final-cta__title">A direct conversation.</h2>
          <p className="final-cta__lede">
            We respond within 24 hours with an honest assessment of whether we can help.
          </p>
          <div className="final-cta__row">
            <Link to="/contact" className="btn btn--primary">
              Start a conversation <span className="arrow"></span>
            </Link>
            <a href="mailto:contact@rslighthouse.com" className="btn btn--ghost">
              contact@rslighthouse.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApproachPage;
